import type { Scenario } from "@radar-sim/schema";

const STORAGE_KEY = "radar-sim:authored-scenarios";

export interface LibraryEntry {
  id: string;
  name: string;
  json: string;
  savedAt: string;
}

/**
 * localStorage-backed scenario library. Stores raw JSON strings rather than
 * parsed objects so unsaved-but-invalid drafts can still round-trip through
 * the editor without losing the user's work.
 */
export function listSavedScenarios(): LibraryEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is LibraryEntry =>
        typeof e === "object" &&
        e !== null &&
        typeof (e as LibraryEntry).id === "string",
    );
  } catch {
    return [];
  }
}

export function saveScenario(entry: LibraryEntry): LibraryEntry[] {
  const all = listSavedScenarios().filter((e) => e.id !== entry.id);
  all.unshift(entry);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  return all;
}

export function deleteScenario(id: string): LibraryEntry[] {
  const all = listSavedScenarios().filter((e) => e.id !== id);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  return all;
}

export function buildLibraryEntry(json: string, scenario: Scenario): LibraryEntry {
  return {
    id: scenario.metadata.id,
    name: scenario.metadata.name,
    json,
    savedAt: new Date().toISOString(),
  };
}
